import { createFileRoute, notFound, redirect } from "@tanstack/react-router"
import { requireChatPermission } from "@/entities/bottom-popup"
import { friendsQueryOptions, loadFriendsRevalidating } from "@/entities/friend"
import { userQueryOptions } from "@/entities/user"
import { FriendBoardPage } from "@/pages/board"

export const Route = createFileRoute("/_authenticated/board/friend/$friendId")({
	params: {
		parse: (raw) => {
			const friendId = Number(raw.friendId)
			if (!Number.isFinite(friendId)) throw notFound()
			return { friendId }
		},
		stringify: (parsed) => ({ friendId: String(parsed.friendId) }),
	},
	// Navigation can reuse a running preload, so the gate must never be skipped.
	preload: false,
	loader: {
		handler: async ({ context, params }) => {
			const { userId } = context
			let friends = await loadFriendsRevalidating(context.queryClient, userId)
			let friend = friends.find(({ id }) => id === params.friendId)

			if (!friend) {
				friends = await context.queryClient
					.query({ ...friendsQueryOptions(userId), staleTime: 0, retry: false })
					.catch(() => [])
				friend = friends.find(({ id }) => id === params.friendId)
			}

			if (!friend) throw redirect({ to: "/home", replace: true })

			const user = await context.queryClient.query({
				...userQueryOptions(userId),
				staleTime: "static",
			})
			// Push taps land here straight from a boot snapshot or a long background,
			// so a cached allow is checked against the server unless it just answered.
			const allowed = await requireChatPermission({
				queryClient: context.queryClient,
				user,
				friend,
				fresh: true,
			})
			if (!allowed) throw redirect({ to: "/home", replace: true })

			return { friend }
		},
		staleReloadMode: "blocking",
	},
	pendingMs: 300,
	component: FriendBoardPageWrapper,
})

function FriendBoardPageWrapper() {
	const { friend } = Route.useLoaderData()
	return <FriendBoardPage friend={friend} />
}
